import React, { useEffect, useRef, useState } from "react";
import { injectIntl } from "react-intl";
import { connect } from "react-redux";
import { Skeleton, Tooltip } from "antd";

import ChatService from "../../services/chat";
import { setProcessInstance } from "../../store/actions/chats";

import "./ChatDescription.scss";

function ChatDescription({
  chat,
  flags,
  intl,
  processInstance,
  setProcessInstance,
}) {
  const processInstanceRef = useRef(null);
  const activityInstanceRef = useRef(null);

  const [showProcessTooltip, setShowProcessTooltip] = useState(false);
  const [showActivityTooltip, setShowActivityTooltip] = useState(false);
  const [loadingProcessInstance, setLoadingProcessInstance] = useState(false);

  const isTruncated = (ref) =>
    ref.current ? ref.current.scrollWidth > ref.current.clientWidth : false;

  const hasProcessInstanceElement = chat?.elements?.some(
    (elem) => elem.description === "process_instance"
  );

  useEffect(() => {
    if (
      processInstance !== undefined ||
      !hasProcessInstanceElement ||
      chat?.newChat
    ) {
      return;
    }

    setLoadingProcessInstance(true);

    ChatService.getProcessInstance(chat.id)
      .then((res) => {
        setProcessInstance(chat.id, res?.data);
      })
      .catch(() => {
        setProcessInstance(chat.id, null);
      })
      .finally(() => {
        setLoadingProcessInstance(false);
      });
  }, [chat?.id]);

  const getFlags = () => {
    const chatFlags = chat?.flags || [];
    const formattedFlags = [];

    for (let i = 0; i < chatFlags.length; i++) {
      const currentFlagModel = flags?.find(
        (elem) => elem.id === chatFlags[i].id
      );

      if (currentFlagModel) {
        formattedFlags.push(
          <span
            className="element-description tag"
            key={`flag-${currentFlagModel.id}`}
            style={{ backgroundColor: currentFlagModel.color }}
          >
            {currentFlagModel.flagName}
          </span>
        );

        if (i < chatFlags.length - 1) {
          formattedFlags.push(
            <span className="bullet" key={`bullet-${currentFlagModel.id}`}>
              {" "}
              &#8226;{" "}
            </span>
          );
        }
      }
    }

    return formattedFlags;
  };

  const getProcessInstanceData = () => {
    const elements = chat?.elements || [];
    const descriptionElements =
      window.Social.getChatConfig().chatDescriptionElements;

    const processInstanceElement = elements.find(
      (elem) => elem.description === "process_instance"
    );

    if (!processInstanceElement) {
      return null;
    }

    const showProcessInstance =
      descriptionElements.includes("process_instance");

    if (!showProcessInstance) {
      return null;
    }

    return {
      id: processInstanceElement.content,
      name: processInstance?.title,
    };
  };

  const getActivityInstance = () => {
    const elements = chat?.elements || [];
    const descriptionElements =
      window.Social.getChatConfig().chatDescriptionElements;

    const activityInstanceElement = elements.find(
      (elem) => elem.description === "activity_instance"
    );

    if (!activityInstanceElement) {
      return null;
    }

    const showActivityInstance =
      descriptionElements.includes("activity_instance");

    if (!showActivityInstance) {
      return null;
    }

    const activity = processInstance?.activities?.find(
      (activity) => activity.id == activityInstanceElement.content
    );

    return {
      id: activityInstanceElement.content,
      name: activity?.title,
    };
  };

  const getCycle = () => {
    const elements = chat?.elements || [];
    const descriptionElements =
      window.Social.getChatConfig().chatDescriptionElements;

    const cycleElement = elements.find((elem) => elem.description === "cycle");

    const showCycle = descriptionElements.includes("cycle");

    if (!showCycle) {
      return null;
    }

    return cycleElement;
  };

  const formattedFlags = window.Social.getChatConfig().allowFlags
    ? getFlags()
    : [];

  const processInstanceData = getProcessInstanceData();
  const activityInstanceData = getActivityInstance();
  const cycle = getCycle();

  const processInstanceDescription = [
    processInstanceData?.id,
    processInstanceData?.name,
  ]
    .filter(Boolean)
    .join(" • ");

  const activityInstanceDescription = [
    activityInstanceData?.name,
    cycle?.content &&
      intl.formatMessage({ id: "chat.cycle" }, { number: cycle.content }),
  ]
    .filter(Boolean)
    .join(" • ");

  const chatEvents = window.Social.getChatEventsConfig
    ? Object.values(window.Social.getChatEventsConfig())
    : [];

  const openFlowEvent = chatEvents.find(
    (elem) => elem.eventName === "OPEN_FLOW"
  );

  const openFormEvent = chatEvents.find(
    (elem) => elem.eventName === "OPEN_FORM"
  );

  const handleProcessInstanceClick = (event) => {
    event.stopPropagation();

    if (openFlowEvent && window.hasOwnProperty("Social")) {
      window.Social.emit(openFlowEvent.eventName, {
        elements: chat.elements,
        processInstance,
      });
    }
  };

  const handleActivityInstanceClick = (event) => {
    event.stopPropagation();

    if (openFormEvent && window.hasOwnProperty("Social")) {
      window.Social.emit(openFormEvent.eventName, {
        elements: chat.elements,
        processInstance,
      });
    }
  };

  return (
    <div className="chat-description">
      {formattedFlags.length > 0 && (
        <div className="chat-description-item">{formattedFlags}</div>
      )}

      {loadingProcessInstance && <Skeleton active paragraph={false} />}

      {!loadingProcessInstance && processInstanceData && (
        <Tooltip
          title={processInstanceDescription}
          visible={showProcessTooltip}
          overlayClassName="social-tooltip"
          onVisibleChange={(visible) => {
            if (visible) {
              setShowProcessTooltip(isTruncated(processInstanceRef));
            } else {
              setShowProcessTooltip(false);
            }
          }}
        >
          <div
            ref={processInstanceRef}
            className={`chat-description-item${
              openFlowEvent ? " clickable" : ""
            }`}
            onClick={openFlowEvent ? handleProcessInstanceClick : undefined}
          >
            {processInstanceDescription}
          </div>
        </Tooltip>
      )}

      {!loadingProcessInstance && activityInstanceDescription && (
        <Tooltip
          title={activityInstanceDescription}
          visible={showActivityTooltip}
          overlayClassName="social-tooltip"
          onVisibleChange={(visible) => {
            if (visible) {
              setShowActivityTooltip(isTruncated(activityInstanceRef));
            } else {
              setShowActivityTooltip(false);
            }
          }}
        >
          <div
            ref={activityInstanceRef}
            className={`chat-description-item${
              openFormEvent ? " clickable" : ""
            }`}
            onClick={openFormEvent ? handleActivityInstanceClick : undefined}
          >
            {activityInstanceDescription}
          </div>
        </Tooltip>
      )}

      {!loadingProcessInstance &&
        !processInstanceData &&
        !activityInstanceData &&
        !cycle && (
          <div className="chat-description-item">
            {window.Social.getChatConfig().emptyDescription}
          </div>
        )}
    </div>
  );
}

const mapStateToProps = ({ ProcessInstances }, { chat }) => ({
  processInstance: ProcessInstances[chat?.id],
});

const mapDispatchToProps = (dispatch) => ({
  setProcessInstance: (chatId, processInstance) =>
    dispatch(setProcessInstance(chatId, processInstance)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(injectIntl(ChatDescription));
